// Servicio de geolocalización optimizado para Safari
export class SafariGeolocationService {
    constructor() {
        this.options = {
            enableHighAccuracy: false, // Safari funciona mejor con false
            timeout: 20000, // Safari necesita más tiempo
            maximumAge: 600000 // 10 minutos de cache para Safari
        };

        this.highAccuracyOptions = {
            enableHighAccuracy: true,
            timeout: 30000, // Aún más tiempo para alta precisión
            maximumAge: 300000
        };
    }

    // Detectar si estamos en Safari
    static isSafari() {
        const ua = navigator.userAgent;
        return /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua);
    }

    // Detectar si estamos en iOS Safari
    static isIOSSafari() {
        const ua = navigator.userAgent;
        return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua);
    }

    // Nuevo método para detectar navegadores embebidos como Instagram
    static isInAppBrowser() {
        const ua = navigator.userAgent.toLowerCase();

        // Detectar navegador de Instagram
        if (ua.indexOf('instagram') > -1) {
            return {
                type: 'instagram',
                name: 'Instagram'
            };
        }

        // Detectar otros navegadores embebidos
        if (ua.indexOf('fb_iab') > -1 || ua.indexOf('fban') > -1 || ua.indexOf('fbav') > -1) {
            return {
                type: 'facebook',
                name: 'Facebook'
            };
        }

        if (ua.indexOf('twitter') > -1) {
            return {
                type: 'twitter',
                name: 'Twitter'
            };
        }

        // No es un navegador embebido
        return false;
    }

    // Verificar si HTTPS está disponible
    static isSecureContext() {
        return window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';
    }

    // Método específico para Safari - intenta múltiples estrategias
    async getLocationSafariCompatible() {
        // Comprobar si estamos en un navegador embebido
        const inAppBrowser = SafariGeolocationService.isInAppBrowser();
        if (inAppBrowser) {
            throw new Error(`Navegador de ${inAppBrowser.name} detectado: La geolocalización no está disponible en navegadores embebidos. Por favor, abre la aplicación en tu navegador normal.`);
        }

        // 1. Verificar que estemos en contexto seguro
        if (!SafariGeolocationService.isSecureContext()) {
            throw new Error('Safari requiere HTTPS para geolocalización');
        }

        // 2. Verificar soporte de geolocalización
        if (!navigator.geolocation) {
            throw new Error('Geolocalización no soportada en este navegador');
        }

        // 3. Para Safari, intentar primero con baja precisión y luego alta
        try {
            // Primer intento: baja precisión (más probable que funcione)
            const lowAccuracyResult = await this._getPositionWithFallback(this.options);

            // Si iOS Safari, devolver resultado inmediatamente
            if (SafariGeolocationService.isIOSSafari()) {
                return {
                    ...lowAccuracyResult,
                    type: 'safari-low-accuracy'
                };
            }

            // Para Safari desktop, intentar mejorar la precisión
            try {
                const highAccuracyResult = await this._getPositionWithFallback(this.highAccuracyOptions);
                return {
                    ...highAccuracyResult,
                    type: 'safari-high-accuracy'
                };
            } catch (highAccuracyError) {
                // Si falla alta precisión, usar baja precisión
                return {
                    ...lowAccuracyResult,
                    type: 'safari-low-accuracy-fallback'
                };
            }

        } catch (error) {
            throw this._handleSafariGeolocationError(error);
        }
    }

    // Método con múltiples intentos para Safari
    async _getPositionWithFallback(options, maxRetries = 2) {
        let lastError;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {

                const position = await new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(() => {
                        reject(new Error(`Timeout después de ${options.timeout}ms (intento ${attempt})`));
                    }, options.timeout);

                    navigator.geolocation.getCurrentPosition(
                        (pos) => {
                            clearTimeout(timeoutId);
                            resolve(pos);
                        },
                        (error) => {
                            clearTimeout(timeoutId);
                            reject(error);
                        },
                        {
                            ...options,
                            timeout: options.timeout - 1000 // Reducir timeout interno
                        }
                    );
                });

                return {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    attempt: attempt
                };

            } catch (error) {
                lastError = error;
                console.warn(`Safari geolocation attempt ${attempt} failed:`, error.message);

                // Si no es el último intento, esperar antes de reintentar
                if (attempt < maxRetries) {
                    await this._sleep(1000 * attempt); // Espera progresiva

                    // Ajustar opciones para el siguiente intento
                    options = {
                        ...options,
                        enableHighAccuracy: false, // Reducir precisión en reintentos
                        timeout: Math.min(options.timeout + 5000, 30000) // Aumentar timeout
                    };
                }
            }
        }

        throw lastError;
    }

    // Método específico para Safari con geocoding optimizado
    async reverseGeocodeSafariCompatible(latitude, longitude) {
        // Safari a veces tiene problemas con CORS, usar solo servicios compatibles
        const safariCompatibleProviders = [
            {
                name: 'locationiq',
                url: `https://us1.locationiq.com/v1/reverse.php?key=${process.env.REACT_APP_LOCATIONIQ_API_KEY}&lat=${latitude}&lon=${longitude}&format=json&accept-language=es`,
                corsProxy: false
            }
        ];

        for (const provider of safariCompatibleProviders) {
            try {
                const result = await this._tryProviderSafariCompatible(provider, latitude, longitude);
                if (result) {
                    return result;
                }
            } catch (error) {
                console.warn(`Safari geocoding failed for ${provider.name}:`, error.message);
            }
        }

        // Fallback para Safari: usar coordenadas
        return {
            city: '',
            state: '',
            country: '',
            displayName: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
            fullAddress: {},
            isCoordinatesOnly: true,
            safariCompatible: true
        };
    }

    async _tryProviderSafariCompatible(provider, latitude, longitude) {
        // Safari-specific fetch configuration
        const fetchOptions = {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'User-Agent': 'ContactaMe-Safari'
            },
            // Safari-specific options
            mode: 'cors',
            credentials: 'omit',
            cache: 'default'
        };

        // Timeout específico para Safari
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // Más tiempo para Safari
        fetchOptions.signal = controller.signal;

        try {
            const response = await fetch(provider.url, fetchOptions);
            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            return this._parseLocationIQResponseSafari(data);

        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error('Timeout en Safari geocoding');
            }
            throw error;
        }
    }

    _parseLocationIQResponseSafari(data) {
        const address = data.address || {};

        // Parsing simplificado para Safari
        let primaryLocation = address.village || address.city || address.town || address.municipality || '';
        let state = address.state || address.state_district || '';

        // Fallback más robusto para Safari
        let displayName = primaryLocation || state || address.country || 'Ubicación detectada';

        return {
            city: primaryLocation,
            state: state,
            country: address.country || '',
            displayName: displayName,
            smartLocation: {
                approximate: false,
                coordinates: {
                    lat: parseFloat(data.lat),
                    lng: parseFloat(data.lon)
                },
                displayName: displayName,
                lastUpdated: new Date().toISOString(),
                originalLocation: data.display_name,
                serviceLocationId: data.place_id,
                source: "GPS-Safari"
            },
            fullAddress: address,
            safariCompatible: true
        };
    }

    _handleSafariGeolocationError(error) {
        let message = 'Error de geolocalización en Safari';

        switch(error.code) {
            case 1: // PERMISSION_DENIED
                message = 'Safari requiere permisos de ubicación. Ve a Configuración > Privacidad > Servicios de Ubicación';
                break;
            case 2: // POSITION_UNAVAILABLE
                message = 'Ubicación no disponible en Safari. Verifica tu conexión y permisos';
                break;
            case 3: // TIMEOUT
                message = 'Tiempo agotado en Safari. Intenta nuevamente';
                break;
            default:
                message = `Error Safari: ${error.message}`;
        }

        return new Error(message);
    }

    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

export const safariGeolocationService = new SafariGeolocationService();
